// https://prototype.sqprod.co/#/docs/squareup.franklin.api.FormBlocker.Element.ButtonElement
import React, { MutableRefObject, useContext, useState } from 'react'

import { useFormContext } from 'react-hook-form'

import { ArcadeButton, Button, ButtonProminence } from '@cash-design-system/react'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import {
  FormBlockerElementButtonElement,
  FormBlockerElementButtonElementStyle,
  FormBlockerElementButtonElementType,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { getBlockerAction } from '@cash-web/shared-flow-feature-main'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

import { isContactSupportButton } from '../../../../util/isContactSupportButton'
import { FormBlockerContext } from '../../FormBlockerContext'

export type ButtonVariants = React.ComponentProps<typeof Button>['variant']
type SpaceButtonProps = { space?: string }
type Props = FormBlockerElementButtonElement & {
  disabled?: boolean
  canSubmit?: boolean
  selectedAction?: MutableRefObject<string | undefined>
} & SpaceButtonProps

const getVariant = (
  action: BlockerAction,
  style?: FormBlockerElementButtonElementStyle,
  type?: FormBlockerElementButtonElementType
): ButtonVariants => {
  // Standardize all "Contact Support" buttons to secondary variant for consistency
  if (isContactSupportButton(action.text)) {
    return 'secondary'
  } else if (action.view_action?.title === 'direct-deposit-authorization-form') {
    return 'primary'
  } else if (type === FormBlockerElementButtonElementType.DESTRUCTIVE) {
    return 'error'
  } else if (style === FormBlockerElementButtonElementStyle.PRIMARY) {
    return 'primary'
  } else if (style === FormBlockerElementButtonElementStyle.SECONDARY) {
    return 'secondary'
  } else if (style === FormBlockerElementButtonElementStyle.TERTIARY) {
    return 'tertiary'
  }

  return 'primary'
}

const getArcadeProminence = (
  action: BlockerAction,
  style?: FormBlockerElementButtonElementStyle,
  type?: FormBlockerElementButtonElementType
): ButtonProminence => {
  // Standardize all "Contact Support" buttons to secondary variant for consistency
  if (isContactSupportButton(action.text)) {
    return 'standard'
  } else if (action.view_action?.title === 'direct-deposit-authorization-form') {
    return 'prominent'
  } else if (type === FormBlockerElementButtonElementType.DESTRUCTIVE) {
    return 'prominent'
  } else if (style === FormBlockerElementButtonElementStyle.PRIMARY) {
    return 'prominent'
  } else if (style === FormBlockerElementButtonElementStyle.SECONDARY) {
    return 'standard'
  } else if (style === FormBlockerElementButtonElementStyle.TERTIARY) {
    return 'subtle'
  }

  return 'prominent'
}

export function ButtonElement({
  action,
  style,
  type,
  disabled,
  canSubmit,
  selectedAction,
}: Props): React.ReactElement | null {
  const context = useContext(FormBlockerContext)
  const { error } = useContext(ErrorContext)
  const form = useFormContext()
  const logCdfEvent = useLogCashWebCdfEvent()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const [isNonPrimaryOrSecondaryButtonLoading, setIsNonPrimaryOrSecondaryButtonLoading] = useState<boolean>(false)

  if (!action) {
    return null
  }

  const blockerAction = getBlockerAction(action)
  if (!blockerAction.isSupported) {
    logCdfEvent(
      WebViewUnsupportedFeature({
        source: 'ButtonElement',
        feature: 'BLOCKER_ACTION',
        action: blockerAction.actionName,
      })
    )
    return null
  }

  if (isArcadeMigrationEnabled) {
    const prominence = getArcadeProminence(action, style, type)
    const isLoading = canSubmit
      ? form?.formState?.isSubmitting && selectedAction?.current === prominence
      : isNonPrimaryOrSecondaryButtonLoading && !error

    return (
      <ArcadeButton
        disabled={disabled}
        isDestructive={type === FormBlockerElementButtonElementType.DESTRUCTIVE}
        isLoading={isLoading}
        prominence={prominence}
        role="button"
        size="cta"
        type={canSubmit ? 'submit' : 'button'}
        onClick={() => {
          if (selectedAction) {
            selectedAction.current =
              type === FormBlockerElementButtonElementType.DESTRUCTIVE &&
              style === FormBlockerElementButtonElementStyle.PRIMARY
                ? 'prominent'
                : prominence
          }
          if (!canSubmit) {
            if (action.submit_action) {
              setIsNonPrimaryOrSecondaryButtonLoading(true)
            }
            context.onFormBlockerAction(action)
          }
        }}
      >
        {action.text}
      </ArcadeButton>
    )
  }

  const variant = getVariant(action, style, type)
  const isLoading = canSubmit
    ? form?.formState?.isSubmitting && selectedAction?.current === variant
    : isNonPrimaryOrSecondaryButtonLoading && !error

  return (
    <Button
      size="large"
      role="button"
      type={canSubmit ? 'submit' : 'button'}
      variant={variant}
      disabled={disabled}
      loading={isLoading}
      onClick={() => {
        if (selectedAction) {
          selectedAction.current =
            type === FormBlockerElementButtonElementType.DESTRUCTIVE &&
            style === FormBlockerElementButtonElementStyle.PRIMARY
              ? 'primary'
              : variant
        }
        if (!canSubmit) {
          if (action.submit_action) {
            setIsNonPrimaryOrSecondaryButtonLoading(true)
          }
          context.onFormBlockerAction(action)
        }
      }}
    >
      {action.text}
    </Button>
  )
}
